import React, { RefObject } from 'react';
import { Dispatch } from 'redux';

import axios from '../../../config/axiosConfig';
import { EmailRequest, LoginRequest, LoginResponse, LOGIN_RESPONSE } from '../../../redux/account/models';
import { fetchRosterStudentsStart, toggleRoster } from '../../../redux/rosters';
import { ErrorResponse, ERROR_RESPONSE, isType, ISubjectGradeInfo } from '../../../shared';
import { TestLaunchResult, TEST_LAUNCH_RESULT } from '../../TestCodeButton/models';
import { SCORE_RESULT, STATISTIC_RESULT } from './Modal.constants';
import { QuizLaunchRequest, RosterExpandable, ScoreResult, StatisticResult } from './Modal.interfaces';
import { WindowType } from './Modal.types';
import gaEvents from '../../../shared/analyticsEvents';

//#region Shared
export const onAfterOpenCallback = (windowType: WindowType, cancelBtn: RefObject<HTMLButtonElement>, okBtn: RefObject<HTMLButtonElement>): void => {
  (windowType === 'DESC') ?
    cancelBtn.current && cancelBtn.current.focus() :
    okBtn.current && okBtn.current.focus();
};
//#endregion Shared

//#region AccountModal Utils
export const createAccountAsync = async(payload: LoginRequest): Promise<LoginResponse|ErrorResponse> => {
  const response = await axios.post<LoginResponse>('api/acctservices/create', {...payload})
    .then((res) => ({...res.data, __typename: LOGIN_RESPONSE}))
    .catch((errMsg: string): ErrorResponse => ({errMsg, __typename: ERROR_RESPONSE}));
  return response;
};

export const processCreateAccount = async(
  payload: LoginRequest,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setErrMsg: React.Dispatch<React.SetStateAction<string>>
): Promise<void> => {
  setIsLoading(true);
  const response = await createAccountAsync(payload);
  if (isType<LoginResponse>(response, LOGIN_RESPONSE)) {
    if (response.success) {
      setSuccess(true);
      gaEvents.onGA4UserAccountCreated();
      setErrMsg('');
    } else {
      setSuccess(false);
      setErrMsg(response.errorMsg as string);
    }
  } else {
    setErrMsg(response.errMsg);
  }
  setIsLoading(false);
};
//#endregion AccountModal Utils

//#region ChangePassword Utils
/**
 * Post req to resetPassword - sets state based on corresponding result
 * @param emailId
 * @param password
 * @param newPassword
 * @param setErrMsg
 * @param setProcessing
 * @param setSuccessful
 * @returns
 */
export const fetchChangePasswordAsync = async(
  emailId: string,
  password: string,
  newPassword: string,
  setErrMsg: React.Dispatch<React.SetStateAction<string>>,
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessful: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> => {
  if (
    !emailId ||
    !password ||
    !newPassword
  ) {
    setProcessing(false);
    setSuccessful(false);
    return;
  }
  const body = {emailId, password, newPassword};
  const response = await axios.post<LoginResponse>('api/acctservices/resetPassword', body)
    .then( res => res.data)
    .catch( () => null);
  if (response) {
    if (response.success) {
      setSuccessful(true);
    } else {
      setSuccessful(false);
      setErrMsg(response.errorMsg as string);
    }
  } else {
    setSuccessful(false);
  }
  setProcessing(false);
};
//#endregion ChangePassword Utils

//#region DeleteAccountModal
/**
 * Delete account request
 * @returns boolean based on success of the call
 */
export const deleteAccountAsync = async(): Promise<boolean> => {
  const response = await axios.post('api/acctServices/delete')
    .then( res => res.status)
    .catch( err => err);
  if (typeof response === 'number' && response >= 200 && response < 400) {
    return true;
  } else {
    return false;
  }
};
//#endregion DeleteAccountModal

//#region Login Utils
export const forgotPasswordAsync = async(payload: EmailRequest): Promise<LoginResponse|ErrorResponse> => {
  const response = await axios.post<LoginResponse>('api/acctservices/forgotpassword', {...payload})
    .then((res) => ({...res.data, __typename: LOGIN_RESPONSE}))
    .catch((errMsg:string) => ({errMsg, __typename: ERROR_RESPONSE}));
  return response;
};

export const processForgotPassword = async(
  payload: EmailRequest,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessful: React.Dispatch<React.SetStateAction<boolean>>,
  setErrMsg: React.Dispatch<React.SetStateAction<string>>
): Promise<void> => {
  setIsLoading(true);
  const response = await forgotPasswordAsync(payload);
  if (isType<LoginResponse>(response, LOGIN_RESPONSE)) {
    if (response.errorMsg) {
      setErrMsg(response.errorMsg as string);
      setSuccessful(false);
    } else {
      setErrMsg('');
      setSuccessful(true);
    }
  } else {
    setErrMsg(response.errMsg);
    setSuccessful(false);
  }
  setIsLoading(false);
};
//#endregion Login Utils

//#region PasswordResetModal Utils
/**
 * Post req to resetPasswordEmail - sets state based on corresponding result
 * @param newPassword
 * @param e
 * @param setErrMsg
 * @param setProcessing
 * @param setSuccessful
 * @returns
 */
export const fetchPasswordResetAsync = async(
  newPassword: string,
  e: string,
  setErrMsg: React.Dispatch<React.SetStateAction<string>>,
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessful: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> => {
  if (!newPassword || !e) {
    setProcessing(false);
    setSuccessful(false);
    return;
  }
  const body = {newPassword, e};
  const response = await axios.post<LoginResponse>('api/acctservices/resetPasswordEmail', body)
    .then( res => res.data)
    .catch( () => null);
  if (response) {
    if (response.success) {
      setSuccessful(true);
    } else {
      setSuccessful(false);
      setErrMsg(response.errorMsg as string);
    }
  } else {
    setSuccessful(false);
  }
  setProcessing(false);
};
//#endregion PasswordResetModal Utils

//#region PublishLogin Utils
export const checkEmailAsync = async(
  emailId: string,
  signal: AbortSignal
): Promise<boolean> => {
  const response = await axios.post<LoginResponse>('api/acctservices/validateId', {emailId }, {signal})
    .then( res => res.data)
    .catch( (error) => {
      if (error.cancelled) {
        return {success: true};
      } else {
        return {success: false};
      }
    });
  return !!response?.success;
};

export const processCreateAccountOnPublish = async(
  payload: LoginRequest,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setErrMsg: React.Dispatch<React.SetStateAction<string>>
): Promise<void> => {
  setIsLoading(true);
  const response = await createAccountAsync(payload);
  if (isType<LoginResponse>(response, LOGIN_RESPONSE) && !response.success) {
    setErrMsg(response.errorMsg as string);
  } else if (isType<ErrorResponse>(response, ERROR_RESPONSE)){
    setErrMsg(response.errMsg);
  } else {
    setErrMsg('');
  }
  setIsLoading(false);
};
//#endregion PublishLogin Utils

//#region Roster Utils

export const onClickExpandAsync = async(
  item: RosterExpandable,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>
): Promise<void> => {
  if (!item.children || item.children.length === 0) {
    dispatch(
      fetchRosterStudentsStart({
        rosterName: item.rosterName,
        rosterID: item.rosterID,
        numStudents: '0',
        expand: true
      })
    );

  } else {
    dispatch(
      toggleRoster(item)
    );
  }
};

//#endregion Roster Utils

//#region TestModal Utils
/**
 *
 * @param testTakerResultID - Returned by Redirect page, should match txtCandidateID
 * @returns Promise<StatisticResult | ErrorResponse>
 */
export const fetchTestLaunchResult = (testTakerResultID: number): Promise<StatisticResult | ErrorResponse> => {
  const response = axios.post<StatisticResult>(
    'api/testLaunch/result',
    testTakerResultID,
    {
      headers: {
        'content-type': 'application/json'
      }
    }
  )
    .then(res => ({...res.data, __typename: STATISTIC_RESULT}))
    .catch((errMsg):ErrorResponse => ({errMsg, __typename: ERROR_RESPONSE}));
  return response;
};

/**
 *
 * @param testTakerResultID - Returned by Redirect page, should match txtCandidateID
 * @returns Promise<ScoreResult | ErrorResponse>
 */
export const fetchTestLaunchScoreResult = async(testTakerResultID: number): Promise<ScoreResult | ErrorResponse> => {
  const response = await axios.post<ScoreResult>(
    'api/testLaunch/scoredResult',
    testTakerResultID,
    {
      headers: {
        'content-type': 'application/json'
      }
    }
  )
    .then(res => ({...res.data, __typename: SCORE_RESULT}))
    .catch((errMsg): ErrorResponse => ({errMsg, __typename: ERROR_RESPONSE}));
  return response;
};

export const fetchQuizSelectors = async( abortController: AbortController,): Promise<ISubjectGradeInfo[] | ErrorResponse> => {
  const response = await axios.post<ISubjectGradeInfo[]>(
    'api/quizLaunch/quizSelectors',
    5,
    {
      headers: {
        'content-type': 'application/json'
      },
      signal: abortController.signal
    }
  )
    .then(res => res.data)
    .catch((errMsg): ErrorResponse => {
      if (errMsg?.cancelled) return {errMsg: 'Request cancelled', __typename: ERROR_RESPONSE};
      return {errMsg, __typename: ERROR_RESPONSE};
    });
  return response;
};

/**
 *
 * @param testTakerResultID - Returned by Redirect page, should match txtCandidateID
 * @returns Promise<StatisticResult | ErrorResponse>
 */
export const fetchQuizLaunch = (request: QuizLaunchRequest): Promise<TestLaunchResult | ErrorResponse> => {
  const response = axios.post<TestLaunchResult>(
    'api/quizLaunch/Launch',
    request,
    {
      headers: {
        'content-type': 'application/json'
      }
    }
  )
    .then(res => ({...res.data, __typename: TEST_LAUNCH_RESULT}))
    .catch((errMsg):ErrorResponse => ({errMsg, __typename: ERROR_RESPONSE}));
  return response;
};

// export const processQuizSelectors = async(
//   setSubjects: React.Dispatch<React.SetStateAction<ISubjectGradeInfo[]>>,
//   setGrades: React.Dispatch<React.SetStateAction<IGrade[]>>,
//   abortController: AbortController
// ): Promise<void> => {
//   const response = await fetchQuizSelectors(abortController);
//   if (!isType<ErrorResponse>(response, ERROR_RESPONSE)) {
//     setSubjects(response);
//     const subjectFound = response.find(sub => sub.isSelected);
//     if (subjectFound) {
//       setGrades(subjectFound.gradeList);
//     }
//   }
// };

//#endregion TestModal Utils